/**
 * Customer invoice — `/finance/customer-invoices/$invoiceId`.
 *
 * Finalization allocates the branch legal series number, emits the posting
 * event and marks the invoice posted in ONE transaction, so an invoice can
 * never carry a number without a posting event or the reverse. Afterwards the
 * document is immutable: finalizing twice or editing answers
 * `409 ar.invoice_immutable`, and **the only correction is a credit note**.
 * The screen says exactly that instead of offering an edit that will fail.
 *
 * A credit note names the invoice line it credits; description, unit price,
 * revenue account and the whole tax snapshot are copied from that line rather
 * than recalculated, so a rate change between the sale and the correction
 * cannot leak into the reversal. Only the credited quantity is caller input,
 * which is why this form asks for nothing else.
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@erp/ui";
import { ArrowLeft } from "lucide-react";

import { surfaceProblem } from "@/components/form";
import { MoneyDisplay } from "@/components/money/money-display";
import { useIdempotentMutation } from "@/features/shared/ops-api";
import { formatQuantity } from "@/lib/format";

import {
  createCreditNote,
  finalizeCreditNote,
  finalizeCustomerInvoice,
  type CreateCreditNoteRequest,
  type CreditNote,
  type CustomerInvoice,
} from "../api";
import { QUANTITY_SCALE, normalizeDecimal, todayIsoDate } from "../decimal";
import {
  DateField,
  DecimalField,
  DefinitionList,
  NoticeBanner,
  PageHeader,
  ProblemBanner,
  StatusBadge,
  TextField,
} from "../components/fields";
import { financeKeys, useCustomerInvoice, usePartyOptions } from "../queries";
import { AR_INVOICE_STATUS_VARIANTS } from "./customer-invoices-page";

export interface CustomerInvoicePageProps {
  invoiceId?: string;
  onBack: () => void;
  canFinalize?: boolean;
  canCreateCreditNote?: boolean;
  canFinalizeCreditNote?: boolean;
  canReadParties?: boolean;
}

export function CustomerInvoicePage({
  invoiceId,
  onBack,
  canFinalize = true,
  canCreateCreditNote = true,
  canFinalizeCreditNote = true,
  canReadParties = true,
}: CustomerInvoicePageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const invoiceQuery = useCustomerInvoice(invoiceId);
  const partiesQuery = usePartyOptions("customer", canReadParties);
  const invoice = invoiceQuery.data;

  const [creditQuantities, setCreditQuantities] = useState<Record<string, string>>({});
  const [creditReason, setCreditReason] = useState("");
  const [creditIssueDate, setCreditIssueDate] = useState(todayIsoDate);
  const [creditNote, setCreditNote] = useState<CreditNote | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const partyName = useMemo(() => {
    if (invoice === undefined) return "";
    return (
      (partiesQuery.data ?? []).find((party) => party.id === invoice.party_id)?.display_name ??
      invoice.party_id
    );
  }, [invoice, partiesQuery.data]);

  const finalizeMutation = useIdempotentMutation<CustomerInvoice, { invoiceId: string }>({
    mutationFn: (input, key) => finalizeCustomerInvoice(input.invoiceId, key),
    onSuccess: (next) => {
      queryClient.setQueryData(financeKeys.customerInvoices.detail(next.id), next);
      void queryClient.invalidateQueries({ queryKey: financeKeys.customerInvoices.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const createNoteMutation = useIdempotentMutation<CreditNote, CreateCreditNoteRequest>({
    mutationFn: (body, key) => createCreditNote(body, key),
    onSuccess: (note) => {
      setCreditNote(note);
      setCreditQuantities({});
      setCreditReason("");
      void queryClient.invalidateQueries({ queryKey: financeKeys.customerInvoices.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const finalizeNoteMutation = useIdempotentMutation<CreditNote, { creditNoteId: string }>({
    mutationFn: (input, key) => finalizeCreditNote(input.creditNoteId, key),
    onSuccess: (note) => {
      setCreditNote(note);
      void queryClient.invalidateQueries({ queryKey: financeKeys.customerInvoices.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  if (invoiceQuery.isLoading) {
    return <div className="p-4 text-sm text-muted-foreground">{t("app.loading", "Loading…")}</div>;
  }

  if (invoice === undefined) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <ProblemBanner>
          {t("finance.customerInvoices.notFound", "This invoice could not be loaded.")}
        </ProblemBanner>
        <Button type="button" variant="outline" onClick={onBack}>
          {t("finance.customerInvoices.backToList", "Back to customer invoices")}
        </Button>
      </div>
    );
  }

  const isDraft = invoice.status === "draft";
  const creditable = invoice.status !== "draft" && invoice.status !== "void";
  const statusLabel = t(`finance.customerInvoices.status.${invoice.status}`, invoice.status);

  const renderQuantity = (value: string): string => {
    try {
      return formatQuantity(value, i18n.language, QUANTITY_SCALE);
    } catch {
      return value;
    }
  };

  const submitCreditNote = () => {
    const lines: { invoice_line_id: string; quantity: string }[] = [];
    for (const line of invoice.lines) {
      const raw = creditQuantities[line.id] ?? "";
      if (raw.trim() === "") continue;
      const quantity = normalizeDecimal(raw, { scale: QUANTITY_SCALE, allowZero: false });
      if (quantity === null) {
        setFormError(
          t(
            "finance.customerInvoices.creditQuantityInvalid",
            "Line {{line}}: enter a non-zero quantity with at most six decimals.",
            { line: line.line_no },
          ),
        );
        return;
      }
      lines.push({ invoice_line_id: line.id, quantity });
    }
    if (lines.length === 0) {
      setFormError(
        t("finance.customerInvoices.creditLinesRequired", "Credit at least one invoice line."),
      );
      return;
    }
    if (creditReason.trim() === "") {
      setFormError(
        t("finance.customerInvoices.creditReasonRequired", "A credit note needs a written reason."),
      );
      return;
    }
    setFormError(null);
    createNoteMutation.mutate({
      invoice_id: invoice.id,
      reason: creditReason.trim(),
      issue_date: creditIssueDate,
      lines,
    });
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={
          invoice.invoice_no === ""
            ? t("finance.customerInvoices.draftTitle", "Draft invoice")
            : invoice.invoice_no
        }
        description={partyName}
        actions={
          <>
            <Button type="button" variant="ghost" onClick={onBack}>
              <ArrowLeft className="size-4 rtl:-scale-x-100" aria-hidden="true" />
              {t("finance.customerInvoices.backToList", "Back to customer invoices")}
            </Button>
            {canFinalize && isDraft ? (
              <Button
                type="button"
                loading={finalizeMutation.isPending}
                onClick={() => finalizeMutation.mutate({ invoiceId: invoice.id })}
              >
                {t("finance.customerInvoices.finalize", "Issue and post")}
              </Button>
            ) : null}
          </>
        }
      />

      <Card>
        <CardContent className="flex flex-col gap-4 pt-6">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge
              status={invoice.status}
              label={statusLabel}
              variants={AR_INVOICE_STATUS_VARIANTS}
            />
            {invoice.posted_at === null ? null : (
              <span className="text-sm text-muted-foreground">
                {t("finance.customerInvoices.postedAt", "Posted")}{" "}
                <span dir="ltr">{invoice.posted_at}</span>
              </span>
            )}
          </div>
          <DefinitionList
            rows={[
              {
                label: t("finance.customerInvoices.issueDate", "Issue date"),
                value: <span dir="ltr">{invoice.issue_date}</span>,
              },
              {
                label: t("finance.customerInvoices.dueDate", "Due date"),
                value: <span dir="ltr">{invoice.due_date}</span>,
              },
              {
                label: t("finance.common.currency", "Currency"),
                value: <span dir="ltr">{invoice.currency}</span>,
              },
              {
                label: t("finance.customerInvoices.netTotal", "Net"),
                value: <MoneyDisplay value={invoice.net_total} />,
              },
              {
                label: t("finance.customerInvoices.taxTotal", "Tax"),
                value: <MoneyDisplay value={invoice.tax_total} />,
              },
              {
                label: t("finance.customerInvoices.grossTotal", "Gross"),
                value: <MoneyDisplay value={invoice.gross_total} />,
              },
              {
                label: t("finance.customerInvoices.allocatedTotal", "Allocated"),
                value: <MoneyDisplay value={invoice.allocated_total} />,
              },
              {
                label: t("finance.customerInvoices.creditedTotal", "Credited"),
                value: <MoneyDisplay value={invoice.credited_total} />,
              },
              {
                label: t("finance.customerInvoices.openAmount", "Open"),
                value: <MoneyDisplay value={invoice.open_amount} />,
              },
            ]}
          />
          {isDraft ? null : (
            <NoticeBanner>
              {t(
                "finance.customerInvoices.immutable",
                "A posted invoice is immutable. The only correction is a credit note.",
              )}
            </NoticeBanner>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("finance.customerInvoices.linesSection", "Lines")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th scope="col" className="p-2 text-start">
                  #
                </th>
                <th scope="col" className="p-2 text-start">
                  {t("finance.customerInvoices.lineDescription", "Description")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("finance.customerInvoices.lineQuantity", "Quantity")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("finance.customerInvoices.lineUnitPrice", "Unit price")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("finance.customerInvoices.lineNet", "Net")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("finance.customerInvoices.lineTax", "Tax")}
                </th>
                {creditable && canCreateCreditNote ? (
                  <th scope="col" className="p-2 text-end">
                    {t("finance.customerInvoices.creditQuantity", "Credit qty")}
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {invoice.lines.map((line) => (
                <tr key={line.id} className="border-b align-top">
                  <td className="p-2 tabular-nums">{line.line_no}</td>
                  <td className="p-2">{line.description}</td>
                  <td dir="ltr" className="p-2 text-end tabular-nums">
                    {renderQuantity(line.quantity)}
                  </td>
                  <td className="p-2 text-end">
                    <MoneyDisplay value={line.unit_price} />
                  </td>
                  <td className="p-2 text-end">
                    <MoneyDisplay value={line.net} />
                  </td>
                  <td className="p-2 text-end">
                    <MoneyDisplay value={line.tax_amount} />
                    <span className="ms-1 text-xs text-muted-foreground">
                      {t("finance.customerInvoices.taxPercent", "{{percent}}%", {
                        percent: line.tax_rate,
                      })}
                    </span>
                  </td>
                  {creditable && canCreateCreditNote ? (
                    <td className="p-2">
                      <DecimalField
                        id={`credit-qty-${line.id}`}
                        label={t(
                          "finance.customerInvoices.creditQuantityLine",
                          "Credit line {{line}}",
                          {
                            line: line.line_no,
                          },
                        )}
                        value={creditQuantities[line.id] ?? ""}
                        onChange={(value) =>
                          setCreditQuantities((current) => ({ ...current, [line.id]: value }))
                        }
                        decimal={{ scale: QUANTITY_SCALE, allowZero: false }}
                        invalidMessage={t(
                          "finance.customerInvoices.quantityInvalid",
                          "Quantities carry at most six decimals and cannot be zero.",
                        )}
                        className="w-32"
                      />
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {creditable && canCreateCreditNote ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("finance.customerInvoices.creditNoteSection", "Credit note")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              {t(
                "finance.customerInvoices.creditNoteHint",
                "Only the credited quantity is your input. Description, price, revenue account and the tax snapshot are copied from the invoice line.",
              )}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextField
                id="credit-reason"
                label={t("finance.customerInvoices.creditReason", "Reason")}
                value={creditReason}
                onChange={setCreditReason}
              />
              <DateField
                id="credit-issue-date"
                label={t("finance.customerInvoices.creditIssueDate", "Issue date")}
                value={creditIssueDate}
                onChange={setCreditIssueDate}
              />
            </div>

            {formError === null ? null : <ProblemBanner>{formError}</ProblemBanner>}

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                loading={createNoteMutation.isPending}
                onClick={submitCreditNote}
              >
                {t("finance.customerInvoices.createCreditNote", "Create draft credit note")}
              </Button>
              {creditNote !== null && creditNote.status === "draft" && canFinalizeCreditNote ? (
                <Button
                  type="button"
                  variant="secondary"
                  loading={finalizeNoteMutation.isPending}
                  onClick={() => finalizeNoteMutation.mutate({ creditNoteId: creditNote.id })}
                >
                  {t(
                    "finance.customerInvoices.finalizeCreditNote",
                    "Issue and post the credit note",
                  )}
                </Button>
              ) : null}
            </div>

            {creditNote === null ? null : (
              <div className="rounded-md border p-3 text-sm" data-testid="credit-note-summary">
                <p className="font-medium">
                  {creditNote.credit_note_no === ""
                    ? t("finance.customerInvoices.creditNoteDraft", "Draft credit note")
                    : creditNote.credit_note_no}
                </p>
                <p className="text-muted-foreground">
                  <MoneyDisplay value={creditNote.gross_total} />{" "}
                  {t(
                    `finance.customerInvoices.creditNoteStatus.${creditNote.status}`,
                    creditNote.status,
                  )}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
